import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, Link as LinkIcon, Sparkles, CheckCircle2, XCircle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { trialApi, TrialIndustryOption } from '@/api/trialApi';
import { recordFunnelEvent } from '@/api/funnelEventApi';
import { TurnstileWidget, TURNSTILE_ENABLED } from '@/components/common/TurnstileWidget';
import { Spinner } from '@/components/ui/spinner';
import { useLanguage } from '@/contexts/LanguageContext';

type Mode = 'upload' | 'url';

const FREE_TRIAL_COPY = {
  ko: {
    badge: '가입 없이 지금 바로 체험',
    heading: '내 이미지로 직접 확인해보세요',
    subtitle: '하루 1회, 무료로 AI가 생성한 ALT 텍스트를 바로 확인할 수 있습니다.',
    uploadTab: '이미지 업로드',
    urlTab: '이미지 URL',
    uploadPreviewAlt: '선택한 이미지 미리보기',
    uploadPrompt: '클릭해서 이미지를 선택하세요 (최대 5MB)',
    contextToggle: '업종/지침 설정해서 체험하기 (선택)',
    industryLabel: '업종',
    industryNone: '선택 안 함',
    instructionLabel: '추가 지침',
    instructionPlaceholder: '예: 캐주얼하고 친근한 톤으로',
    englishOutputLabel: '영어로 결과 받기',
    contextNote: '체험용 간이 설정이며, 실제 업무에서 생성하실 때는 "AI 컨텍스트 프로필"에서 10개 이상의 상세 설정 기능을 이용하실 수 있습니다.',
    consentText: '업로드한 이미지는 ALT 텍스트 생성을 위해 OpenAI 등 해외 AI 서비스로 전송되어 처리됩니다. 이에 동의합니다.',
    consentRequiredError: '이미지 처리를 위한 해외 AI 서비스 전송 동의가 필요합니다.',
    fileRequiredError: '이미지 파일을 선택해주세요.',
    urlRequiredError: '이미지 URL을 입력해주세요.',
    genericError: '체험 요청 처리 중 오류가 발생했습니다.',
    genericErrorRetry: '체험 요청 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
    submitGenerating: '생성 중...',
    submitExhausted: '오늘 무료 체험을 모두 사용했어요',
    submitDefault: 'ALT 텍스트 생성해보기',
    exhaustedNote: '회원가입하면 매일, 월 40건까지 무료로 이용하실 수 있습니다.',
    resultBadge: '생성 완료',
    resultImageAlt: '체험에 사용한 이미지',
    resultLabel: '생성된 ALT 텍스트',
    resultFooterNote: '회원가입하면 업종·톤에 맞춘 맞춤형 ALT를 매일, 월 40건까지 무료로 받아보실 수 있습니다.',
    signupCta: '무료로 회원가입하기',
    retryCta: '다른 이미지로 다시 해보기',
  },
  en: {
    badge: 'Try it now, no signup required',
    heading: 'See it in action with your own image',
    subtitle: 'Try the AI-generated ALT text for free, once a day.',
    uploadTab: 'Upload Image',
    urlTab: 'Image URL',
    uploadPreviewAlt: 'Selected image preview',
    uploadPrompt: 'Click to select an image (max 5MB)',
    contextToggle: 'Try with industry/instructions (optional)',
    industryLabel: 'Industry',
    industryNone: 'None',
    instructionLabel: 'Additional instructions',
    instructionPlaceholder: 'e.g. Use a casual, friendly tone',
    englishOutputLabel: 'Receive result in English',
    contextNote: 'This is a simplified trial setting. For real use, you can access 10+ detailed settings in the "AI Context Profile".',
    consentText: 'Uploaded images are sent to an overseas AI service (e.g. OpenAI) for ALT text generation. I agree to this.',
    consentRequiredError: 'You must agree to sending the image to an overseas AI service for processing.',
    fileRequiredError: 'Please select an image file.',
    urlRequiredError: 'Please enter an image URL.',
    genericError: 'An error occurred while processing your trial request.',
    genericErrorRetry: 'An error occurred while processing your trial request. Please try again later.',
    submitGenerating: 'Generating...',
    submitExhausted: "You've used today's free trial",
    submitDefault: 'Generate ALT text',
    exhaustedNote: 'Sign up for free to get up to 40 images per month, every day.',
    resultBadge: 'Complete',
    resultImageAlt: 'Image used for the trial',
    resultLabel: 'Generated ALT text',
    resultFooterNote: 'Sign up to get custom ALT text tailored to your industry and tone, up to 40 images per month for free.',
    signupCta: 'Sign up for free',
    retryCta: 'Try another image',
  },
} as const;

export function FreeTrialWidget() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = FREE_TRIAL_COPY[language];
  const [mode, setMode] = useState<Mode>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null);
  // 결과 화면("체험에 사용한 이미지")용 - 업로드 미리보기(uploadPreviewUrl)와 별도 state.
  // 두 모드가 previewUrl 하나를 공유하면 URL 모드에서 세팅한 값이 업로드 탭 드롭존에도
  // 그대로 노출되는 버그가 있었음(2026-08-13 랜딩페이지 무료체험 버그 리포트).
  const [resultPreviewUrl, setResultPreviewUrl] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const [guestToken, setGuestToken] = useState<string | null>(null);

  // 컨텍스트 프로필(선택) - 회원가입 없이도 결과를 업종/지침에 맞춰 미리 체험해볼 수 있게 함.
  // 서버에 저장하지 않고 이번 요청에만 사용됨(방문 간 기억 안 함).
  const [showContextOptions, setShowContextOptions] = useState(false);
  const [industries, setIndustries] = useState<TrialIndustryOption[]>([]);
  const [industryCode, setIndustryCode] = useState('');
  const [customInstruction, setCustomInstruction] = useState('');
  const [wantEnglishOutput, setWantEnglishOutput] = useState(language === 'en');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    trialApi.getIndustries().then(setIndustries).catch(() => {
      // 업종 목록 조회 실패해도 체험 자체는 계속 가능해야 하므로 조용히 무시
      setIndustries([]);
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setError(null);
    const reader = new FileReader();
    reader.onload = () => setUploadPreviewUrl(reader.result as string);
    reader.readAsDataURL(f);
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!consent) {
      setError(t.consentRequiredError);
      return;
    }
    if (mode === 'upload' && !file) {
      setError(t.fileRequiredError);
      return;
    }
    if (mode === 'url' && !urlInput.trim()) {
      setError(t.urlRequiredError);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    recordFunnelEvent('TRIAL', 'START', guestToken);

    const outputLanguage = wantEnglishOutput ? 'en' : undefined;

    try {
      const res = mode === 'upload'
        ? await trialApi.generateFromUpload(file as File, industryCode || undefined, customInstruction.trim() || undefined, turnstileToken || undefined, outputLanguage)
        : await trialApi.generateFromUrl(urlInput.trim(), industryCode || undefined, customInstruction.trim() || undefined, turnstileToken || undefined, outputLanguage);

      if (res.guestToken) setGuestToken(res.guestToken);

      if (!res.success) {
        if (res.errorCode === 'RATE_LIMIT_EXCEEDED' || res.errorCode === 'GLOBAL_RATE_LIMIT_EXCEEDED') {
          setExhausted(true);
        }
        setError(res.message || t.genericError);
        return;
      }

      setResultPreviewUrl(mode === 'upload' ? uploadPreviewUrl : urlInput.trim());
      setResult(res.altText || '');
      recordFunnelEvent('TRIAL', 'RESULT_SHOWN', res.guestToken || guestToken);
    } catch (err: any) {
      const code = err.response?.data?.errorCode;
      if (code === 'RATE_LIMIT_EXCEEDED' || code === 'GLOBAL_RATE_LIMIT_EXCEEDED') {
        setExhausted(true);
      }
      setError(err.response?.data?.message || t.genericErrorRetry);
    } finally {
      setLoading(false);
    }
  };

  const handleSignupClick = () => {
    recordFunnelEvent('TRIAL', 'SIGNUP_CTA_CLICK', guestToken);
    navigate('/signup');
  };

  const resetForm = () => {
    setResult(null);
    setError(null);
    setFile(null);
    setUploadPreviewUrl(null);
    setResultPreviewUrl(null);
    setUrlInput('');
  };

  return (
    <section id="free-trial" data-ga-section="free_trial" className="py-16 bg-gradient-to-b from-white to-blue-50/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">{t.badge}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {t.heading}
          </h2>
          <p className="text-gray-600">
            {t.subtitle}
          </p>
        </div>

        <Card className="max-w-xl mx-auto shadow-lg border-2">
          <CardContent className="p-6 sm:p-8">
            {!result ? (
              <>
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => switchMode('upload')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      mode === 'upload' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Upload className="inline h-4 w-4 mr-1.5 -mt-0.5" />
                    {t.uploadTab}
                  </button>
                  <button
                    type="button"
                    onClick={() => switchMode('url')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      mode === 'url' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <LinkIcon className="inline h-4 w-4 mr-1.5 -mt-0.5" />
                    {t.urlTab}
                  </button>
                </div>

                {mode === 'upload' ? (
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-8 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={loading || exhausted}
                    />
                    {uploadPreviewUrl ? (
                      <img src={uploadPreviewUrl} alt={t.uploadPreviewAlt} className="max-h-40 rounded-lg object-contain" />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400" />
                        <span className="text-sm text-gray-500">{t.uploadPrompt}</span>
                      </>
                    )}
                  </label>
                ) : (
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    disabled={loading || exhausted}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                )}

                {/* 컨텍스트 프로필(선택) - 회원가입 없이도 업종/지침을 반영한 결과를 미리 체험 */}
                <div className="mt-4 border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setShowContextOptions((v) => !v)}
                    disabled={loading || exhausted}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                  >
                    <span>{t.contextToggle}</span>
                    {showContextOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {showContextOptions && (
                    <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t.industryLabel}</label>
                        <select
                          value={industryCode}
                          onChange={(e) => setIndustryCode(e.target.value)}
                          disabled={loading || exhausted}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                        >
                          <option value="">{t.industryNone}</option>
                          {industries.map((opt) => (
                            <option key={opt.itemKey} value={opt.itemKey}>{opt.itemLabel}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t.instructionLabel}</label>
                        <input
                          type="text"
                          value={customInstruction}
                          onChange={(e) => setCustomInstruction(e.target.value)}
                          placeholder={t.instructionPlaceholder}
                          maxLength={200}
                          disabled={loading || exhausted}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      {language === 'en' && (
                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={wantEnglishOutput}
                            onChange={(e) => setWantEnglishOutput(e.target.checked)}
                            disabled={loading || exhausted}
                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                          />
                          {t.englishOutputLabel}
                        </label>
                      )}
                      <p className="text-xs text-gray-400">
                        {t.contextNote}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-2 mt-4">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    disabled={loading || exhausted}
                    className="w-4 h-4 mt-0.5 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                  />
                  <span className="text-xs text-gray-500">
                    {t.consentText}
                  </span>
                </div>

                <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />

                {error && (
                  <p className="text-sm text-red-600 mt-3 flex items-center gap-1.5">
                    <XCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                  </p>
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={loading || exhausted || (mode === 'upload' ? !file : !urlInput.trim()) || !consent || (TURNSTILE_ENABLED && !turnstileToken)}
                  className="w-full mt-4"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t.submitGenerating}
                    </>
                  ) : exhausted ? (
                    t.submitExhausted
                  ) : (
                    <>
                      {t.submitDefault}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                {exhausted && (
                  <p className="text-xs text-gray-500 text-center mt-3">
                    {t.exhaustedNote}
                  </p>
                )}
              </>
            ) : (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full mb-4">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">{t.resultBadge}</span>
                </div>
                {resultPreviewUrl && (
                  <img src={resultPreviewUrl} alt={t.resultImageAlt} className="max-h-48 mx-auto rounded-lg object-contain mb-4" />
                )}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-left mb-4">
                  <p className="text-xs text-primary font-medium mb-1 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {t.resultLabel}
                  </p>
                  <p className="text-primary font-semibold">{result}</p>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  {t.resultFooterNote}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={handleSignupClick} size="lg" className="flex-1">
                    {t.signupCta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="lg" className="flex-1" onClick={resetForm}>
                    {t.retryCta}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
